// agentMode.js — AGENT MODE: the workspace TUI as the whole shell
// (WS-AGENT-MODE-1). Nika 2026-10-23: "we have workspace and tui. now we need
// to add workspace agent-mode which is exact replication of TUI in real
// workspace ui". Her choices: a real DOMQL replica (real DOM per row, card and
// field; brand tokens; the mono face), a WHOLE-SHELL mode (the Agent frame
// takes the viewport in place of the header, the rail and the home; it
// persists per person) and ONE shared source with the TUI
// (`@symbo.ls/workspace-model` — functions/agentModeRuntime.js).
//
// ONE state key, `root.agentMode` (boolean): the person's choice.
//   on   the Agent frame (components/AgentModeFrame.js) covers the viewport
//   off  the normal shell
// A LINK enters it for this visit without a write: `?mode=agent`
// (`root._agentModeLink`, read at boot — agentModeFromSearch). Leaving is
// always a choice and is written (setAgentMode(false)).
//
// PERSISTED PER PERSON, the assistantMode law (functions/assistantMode.js):
// localStorage paints first (`symbols_agent_mode`); the `user_preferences`
// row (`agentMode`) follows the person to every device and wins once it has
// loaded (functions/loadPrivateData.js); a device choice with no record
// migrates up. Writes are decisions: at once, single-flight on the queue every
// writer of the row shares (`root.__navbarSaveQueue`, keyed the same way),
// the payload composed at SEND time from the live row, a superseded decision
// sends nothing.
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained — the few literals are restated.

// True while the Agent frame is the shell: the person's choice or a link.
export const agentModeShown = (root) => {
  const r = root && typeof root === 'object' ? root : {}
  const chosen = r.agentMode === true
  const link = r._agentModeLink === true
  return chosen || link
}

// `?mode=agent` in a query string (with or without the leading '?').
export const agentModeFromSearch = (search) => {
  try {
    const q = String(search || '')
    const params = new URLSearchParams(q.startsWith('?') ? q.slice(1) : q)
    return params.get('mode') === 'agent'
  } catch (_) {
    return false
  }
}

// The stored choice on this device: true | false, or null when never chosen.
export const readStoredAgentMode = (storage) => {
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const v = store ? store.getItem('symbols_agent_mode') : null
    return v === '1' ? true : v === '0' ? false : null
  } catch (_) {
    return null
  }
}

// Boot reconcile, once the user_preferences row has landed. PURE:
//   { apply: bool }    the record holds a choice that differs from the state
//   { migrate: bool }  no record choice; this device holds one — persist it up
//   null               nothing to do
export const reconcileAgentModeFromPrefs = (current, prefs, stored) => {
  const recorded = prefs && typeof prefs === 'object' ? prefs.agentMode : undefined
  if (recorded === true || recorded === false) {
    return recorded !== (current === true) ? { apply: recorded } : null
  }
  return stored === true || stored === false ? { migrate: stored } : null
}

// setAgentMode(on) via el.call — or setAgentMode(root, on, sdk) from shell
// code (the boot migrate). Leaving also ends a link visit.
export function setAgentMode(a, b, c) {
  const viaCall = typeof a === 'boolean' || a == null
  const value = viaCall ? a : b
  const self = this && typeof this === 'object' ? this : null
  const root = viaCall ? (self && self.state && self.state.root) || null : a
  const sdk = viaCall
    ? (self && (self.sdk || (self.context && self.context.sdk))) || null
    : c || null
  if (value !== true && value !== false) {
    console.warn(
      `[shell] setAgentMode ignored invalid value "${String(value)}" — accepted values: true | false`
    )
    return
  }
  if (!root || typeof root.update !== 'function') return
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  const patch = { agentMode: value }
  if (!value) patch._agentModeLink = false
  if (prefs && prefs.agentMode !== value) patch.userPrefs = { ...prefs, agentMode: value }
  const wasLink = root._agentModeLink === true
  root.update(patch, { preventFetch: true })
  // Leaving a `?mode=agent` visit drops the link from the address, so a reload
  // keeps the person's choice instead of entering again (the shell's router).
  if (!value && wasLink && self && typeof self.router === 'function') {
    const path = self.call('agentModePathWithoutLink')
    if (path) self.router(path, self.getRoot(), {}, { replace: true })
  }
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('symbols_agent_mode', value ? '1' : '0')
  } catch (err) {
    console.warn('[shell] agent-mode cache write failed:', err && err.message)
  }
  if (!sdk || typeof sdk.execute !== 'function') return
  const w = root.workspace
  const key =
    String(
      (w && (w.id || w._id)) || (root.jwtClaims && root.jwtClaims.active_workspace_id) || ''
    ) || 'user'
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  root.__agentModeSeq = (root.__agentModeSeq || 0) + 1
  const seq = root.__agentModeSeq
  const prior = queue.get(key) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      if (seq !== root.__agentModeSeq) return null
      // PER-PATH PATCH — names only `agentMode` (CORE-USERPREFS-PATCH-PER-PATH-1).
      const row = await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch: { set: { agentMode: value } }
      })
      if (seq === root.__agentModeSeq) {
        const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
        root.update(
          {
            userPrefs: now
              ? { ...now, agentMode: value }
              : row && typeof row === 'object'
                ? row
                : { agentMode: value }
          },
          { preventFetch: true }
        )
      }
      return row
    })
  queue.set(key, task)
  return task.catch((err) => {
    console.warn('[shell] user_preferences agentMode save failed:', err && err.message)
    return null
  })
}

// The header's entry and the frame's leave control: flip the choice.
export function toggleAgentMode() {
  const root = this && this.state && this.state.root
  if (!root) return
  const on = root.agentMode === true || root._agentModeLink === true
  return this.call('setAgentMode', !on)
}

// The current address without the `mode=agent` link parameter, or null when
// it carries none. (A read of the location; navigation is the router's.)
export const agentModePathWithoutLink = (loc) => {
  try {
    const l = loc || (typeof window !== 'undefined' ? window.location : null)
    if (!l) return null
    const params = new URLSearchParams(String(l.search || '').replace(/^\?/, ''))
    if (params.get('mode') !== 'agent') return null
    params.delete('mode')
    const q = params.toString()
    return `${l.pathname}${q ? `?${q}` : ''}${l.hash || ''}`
  } catch (_) {
    return null
  }
}
